import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { MoreHorizontal } from "lucide-react";
import { ShowBtn } from "@/components/Show";
import Grid from "@/components/Grid";
import { CreateLoaderInput } from "@/components/Loaders";
import { DeleteBtn } from "@/components/Delete";
import { TypeTableProps } from "@/App/Plans/Table";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { ComponentSwitchToggleStatus } from "@/App/Courses/ActivedOrDisabled";
import { Badge } from "@/components/ui/badge";
import { UpdateBtn } from "../Update";
import { TableDataProps } from "@/pages/AbsenceTypes";
import {
  setStatusAbsenceType,
  getShowAbsenceType,
  deleteAbsenceTypeData,
} from "@/Api/absenceTypes";
import { AbsenceUnitConst, AbsenceUnitLabel } from "@/types/absence";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
}: TypeTableProps<TableDataProps>) => {
  const { hasPermission } = usePermissions();
  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Unidade</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Duração</TableHead>
            <TableHead className="text-center">Documento</TableHead>
            <TableHead className="text-center">Ativo</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data?.map((item, index) => {
            return (
              <TableRow key={item?.id}>
                <TableCell className="truncate max-w-80">
                  {item?.unity?.name || "-"}
                </TableCell>
                <TableCell className="truncate max-w-80">
                  {item?.name}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={cn(
                      "whitespace-nowrap",
                      item?.unit === AbsenceUnitConst.PARTIAL
                        ? "border-violet-200 bg-violet-100 text-violet-700 dark:border-violet-500/30 dark:bg-violet-500/15 dark:text-violet-400"
                        : "border-blue-200 bg-blue-100 text-blue-700 dark:border-blue-500/30 dark:bg-blue-500/15 dark:text-blue-400"
                    )}
                  >
                    {AbsenceUnitLabel[item?.unit] || "-"}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Badge
                    variant="outline"
                    className={cn(
                      "whitespace-nowrap",
                      item?.requires_document
                        ? "border-amber-200 bg-amber-100 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-400"
                        : "border-emerald-200 bg-emerald-100 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-400"
                    )}
                  >
                    {item?.requires_document ? "Exige" : "Não exige"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <ComponentSwitchToggleStatus
                    defaultChecked={!!item?.is_active}
                    params={{
                      func: setStatusAbsenceType,
                      funcParams: item?.uuid,
                    }}
                    updateFront={() =>
                      updateFront((prev) => {
                        return {
                          ...prev,
                          data: prev?.data?.map((values) => {
                            if (values?.uuid === item?.uuid)
                              return {
                                ...values,
                                is_active: !values?.is_active,
                              };
                            return values;
                          }),
                        };
                      })
                    }
                  />
                </TableCell>
                <TableCell className="flex justify-center">
                  <div className="flex gap-3 w-full justify-center">
                    <If condition={hasPermission("manage-absence-types")}>
                      <UpdateBtn
                        uuid={item?.uuid}
                        updateFront={(response) => {
                          updateFront((prev) => {
                            prev?.data?.splice(index, 1, response);
                            return {
                              ...prev,
                              data: [...prev.data],
                            };
                          });
                        }}
                      />
                      <DeleteBtn
                        params={{
                          func: deleteAbsenceTypeData,
                          funcParams: item?.uuid,
                        }}
                        updateFront={() =>
                          updateFront((prev) => ({
                            ...prev,
                            data: [
                              ...prev?.data?.filter(
                                (data) => data?.uuid !== item?.uuid
                              ),
                            ],
                          }))
                        }
                        referenceLabel={item?.name}
                      />
                    </If>
                    <ShowBtn
                      labels={{ title: "Visualizar Tipo de Abono" }}
                      params={{
                        func: getShowAbsenceType,
                        funcParams: item?.uuid,
                      }}
                      render={(responseApi: TableDataProps) => (
                        <div className="w-full h-auto">
                          <div className="flex flex-col gap-3">
                            <Grid.Item name="Nome" flex={0}>
                              {responseApi?.name}
                            </Grid.Item>
                            <Grid.Item name="Unidade" flex={0}>
                              {responseApi?.unity?.name || "-"}
                            </Grid.Item>
                            <Grid.Item name="Duração" flex={0}>
                              {AbsenceUnitLabel[responseApi?.unit] || "-"}
                            </Grid.Item>
                            <Grid.Item name="Descrição" flex={0}>
                              {responseApi?.description || "-"}
                            </Grid.Item>
                            <Grid.Item name="Exige documento" flex={0}>
                              {responseApi?.requires_document ? "Sim" : "Não"}
                            </Grid.Item>
                            <Grid.Item name="Conta no banco de horas" flex={0}>
                              {responseApi?.counts_as_worked_hours
                                ? "Sim"
                                : "Não"}
                            </Grid.Item>
                          </div>
                        </div>
                      )}
                      loader={<CreateLoaderInput lines={5} />}
                    />
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {dataApi?.data?.length < 1 && (
            <TableRow>
              <TableCell colSpan={6}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data?.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
